import createEvent from './domEvents/createEvent';

// https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/mozInputSource
const MozInputSource = {
  UNKNOWN: 0,
  MOUSE: 1,
  PEN: 2,
  ERASER: 3,
  CURSOR: 4,
  TOUCH: 5,
  KEYBOARD: 6,
};

// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
let passiveSupported = false;

try {
  const options = Object.defineProperty({}, 'passive', {
    get() {
      passiveSupported = true;
    },
  });

  window.addEventListener('f', null, options);
} catch (err) { }

function normalizeOptions(options = {}) {
  if (passiveSupported) {
    return options;
  }
  return !!options.capture;
}

class EventHandler {
  constructor(element, event, handler, options = {}) {
    this.element = element;
    this.event = event;
    this.handler = handler;
    this.options = normalizeOptions(options);
    this.isListening = false;
  }

  start() {
    if (this.isListening === false) {
      this.element.addEventListener(this.event, this.handler, this.options);
      this.isListening = true;
    }
    return this;
  }

  stop() {
    if (this.isListening) {
      this.element.removeEventListener(this.event, this.handler, this.options);
      this.isListening = false;
    }
    return this;
  }

  destroy() {
    this.stop();

    this.element = undefined;
  }
}

function isTouchEvent(event) {
  return event.type.indexOf('touch') === 0;
}

function isMouseEvent(event) {
  return event.type === 'click' || event.type.indexOf('mouse') === 0;
}

function testIsFakeMozMouseEvent(event) {
  return (
    event.mozInputSource === MozInputSource.TOUCH
    && isMouseEvent(event)
  );
}

function getWorkingTouchListForEvent(event) {
  const { changedTouches, touches } = event;
  if (changedTouches && changedTouches.length > 0) {
    return changedTouches;
  }
  return touches;
}

function getEventClientCoords(event) {
  if (typeof event.clientX === 'undefined') {
    let pageXOwner = event;

    if (isTouchEvent(event) && !event.pageX) {
      const touchList = getWorkingTouchListForEvent(event);
      pageXOwner = touchList[0];
    }

    return {
      clientX: pageXOwner.pageX - window.pageXOffset,
      clientY: pageXOwner.pageY - window.pageYOffset,
    };
  }

  return {
    clientX: event.clientX,
    clientY: event.clientY,
  };
}

function blockEvent(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

export {
  EventHandler,
  blockEvent,
  getEventClientCoords,
  getWorkingTouchListForEvent,
  testIsFakeMozMouseEvent,
  isMouseEvent,
  isTouchEvent,
  createEvent,
};
